import { CancelToken } from 'apisauce';
import { CancelTokenSource } from 'axios';
import * as R from 'ramda';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import useSearchParams from '../../../hooks/useSearchParams';
import { recoverValuesFromSearchParams } from '../../Crud/useFilter';
import { ParamValues } from '../../Crud/useFilter.types';
import useDebounceFunction from '../useDebouncedFunction';
import { Fetcher } from '../useList.types';
import {
  clearSelecteds,
  fetchMoreResult,
  fetchPageResult,
  fetchingMore,
  fetchingPage,
  infinityListInitialState,
  infinityListReducer,
  removeListItem,
  replaceAllItems,
  resetState,
  selectAll,
  selectAllWithParents,
  selectItem,
  selectItems,
  updateCustomCount,
  updateListItem,
  updateRawCount,
} from './infinityList.state';
import { InfinityListManager, ListConfig, ListOptions } from './infinityList.types';
import { computeParentToggleSelection } from './infinityList.utils';

const DEFAULT_ITEMS_PER_PAGE = 10;

/**
 * @deprecated use useInfiniteListWithCheckbox
 */
const useInfinityList = <T>(
  fetcher: Fetcher<T>,
  listOptions?: ListOptions,
  config: ListConfig = {
    shouldFetch: true,
    searchParamsFormatter: undefined,
    customCount: undefined,
    rawCount: undefined,
  },
): InfinityListManager<T> => {
  const cancelTokenRef = useRef<CancelTokenSource>();

  const location = useLocation();
  const { searchParams } = useSearchParams(undefined, {
    searchParamsToIgnore: config.searchParamsToNotTrigger,
  });

  const [state, dispatch] = useReducer(infinityListReducer<T>(), infinityListInitialState);

  const itemsPerPage = listOptions?.itemsPerPage || DEFAULT_ITEMS_PER_PAGE;

  const searchParamsFormatter = (values: Record<string, any> = {}) => {
    const valuesWithoutUnnecessaryParams = { ...values };
    config.searchParamsToNotTrigger?.forEach(
      (param) => delete valuesWithoutUnnecessaryParams[param],
    );

    if (config.searchParamsFormatter) {
      return config.searchParamsFormatter(valuesWithoutUnnecessaryParams);
    }

    return valuesWithoutUnnecessaryParams;
  };

  const fetchMore = useCallback(async () => {
    if (state.isLoadingMore || !state.hasMore) {
      return;
    }

    const params = recoverValuesFromSearchParams(location.search, searchParamsFormatter);
    dispatch(fetchingMore());
    const response = await fetcher({
      count: itemsPerPage,
      page: state.page,
      params: { ...listOptions, ...params },
    });

    dispatch(fetchMoreResult(response));
  }, [searchParams, fetcher, itemsPerPage, state.page, listOptions, config]);

  const fetchPage = useCallback(
    async ({ isMounted, params }: { isMounted?: boolean; params?: ParamValues }) => {
      cancelTokenRef.current?.cancel();

      const source = CancelToken.source();
      cancelTokenRef.current = source;

      dispatch(fetchingPage());

      const response = await fetcher({
        count: itemsPerPage,
        page: 0,
        params: { ...listOptions, ...params },
        config: { cancelToken: source.token },
      });

      if (isMounted) {
        dispatch(fetchPageResult({ response, page: 0 }));
      }
    },
    [config, fetcher, itemsPerPage, listOptions, cancelTokenRef],
  );

  const toggleSelect = useCallback((key: string) => dispatch(selectItem(key)), []);

  const toggleSelectBatch = useCallback((keys: string[]) => dispatch(selectItems(keys)), []);

  const toggleSelectParent = (childIds: string[]) => {
    dispatch(
      selectItems(computeParentToggleSelection({ currentSelected: state.selecteds, childIds })),
    );
  };

  const toggleSelectAllWithParents = useCallback(
    (field?: string) => dispatch(selectAllWithParents(field)),
    [],
  );

  const toggleSelectAll = useCallback((field?: string) => dispatch(selectAll(field)), []);

  const updateItem = useCallback((index: number, item: T) => {
    dispatch(updateListItem(index, item));
  }, []);

  const changeCustomCount = (customCount: number) => {
    dispatch(updateCustomCount(customCount));
  };

  const changeRawCount = (rawCount: number) => {
    dispatch(updateRawCount(rawCount));
  };

  const removeItem = useCallback((index: number) => {
    dispatch(removeListItem(index));
  }, []);

  const replaceItems = useCallback((items: T[]) => {
    dispatch(replaceAllItems(items));
  }, []);

  const { handleDebounceFunction } = useDebounceFunction<Promise<void>>();

  useEffect(() => {
    let isMounted = true;
    const source = CancelToken.source();

    if (R.isNil(config.shouldFetch) || config.shouldFetch) {
      handleDebounceFunction(async () => {
        dispatch(clearSelecteds());
        await fetchPage({
          isMounted,
          params: recoverValuesFromSearchParams(location.search, searchParamsFormatter),
        });
      });
    }

    return () => {
      isMounted = false;
      source.cancel();
    };
    // o problema de looping é devido ao fetcher
  }, [location.search, config.shouldFetch]);

  useEffect(() => {
    if (config.customCount) {
      changeCustomCount(config.customCount);
    }
  }, [config.customCount]);

  useEffect(() => {
    if (config.rawCount) {
      changeRawCount(config.rawCount);
    }
  }, [config.rawCount]);

  return {
    state,
    updateItem,
    removeItem,
    replaceAllItems: replaceItems,
    changeCustomCount,
    changeRawCount,
    loadMoreRows: fetchMore,
    reload: (params?: ParamValues) =>
      fetchPage({
        isMounted: true,
        params: {
          ...recoverValuesFromSearchParams(location.search, searchParamsFormatter),
          ...params,
        },
      }),
    reset: () => dispatch(resetState()),
    checkbox: {
      toggleSelect,
      toggleSelectBatch,
      toggleSelectParent,
      toggleSelectAll,
      toggleSelectAllWithParents,
      isAllItemsSelected:
        !!state.selecteds &&
        state.selecteds.length > 0 &&
        state.items.length === state.selecteds.length,
    },
  };
};

export default useInfinityList;
